import { Injectable, effect, inject, untracked } from '@angular/core'

import { Store } from '@ngrx/store'

import { type FlxModalConfig, FlxModalService } from '@flowx/angular-ui-toolkit'
import { DEFAULT_LANGUAGE, DEFAULT_LOCALE } from '@flowx/core-sdk'
import {
  StartDataDialogComponent,
  StartDataFromVersion,
} from '@flowx/designer/modules/process/components/start-data-dialog/start-data-dialog.component'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { FlxThemesLoaderService } from '@flowx/designer/modules/theme/services/themes-loader.service'
import { LocaleService } from '@flowx/designer/modules/ui-designer/services/locale.service'
import {
  FlxUiFlowRunDialogComponent,
  type UiFlowRunDialogData,
} from '@flowx/designer/modules/ui-flow/components/ui-flow-run-dialog/ui-flow-run-dialog.component'
import {
  ApplicationResourceType,
  matchesEntryPoint,
} from '@flowx/designer/shared/entities/application.entity'
import {
  EntryPointLaunchResult,
  StartProcessService,
  showEntryPointNotSet,
} from '@flowx/designer/shared/services/start-process.service'
import {
  selectAppId,
  selectAppVersionId,
  selectWorkspaceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'

const START_DIALOG_CONFIG: FlxModalConfig = {
  closeOnBackdropClick: true,
  backdropClass: 'overlay-backdrop',
}

/** What Run on Config starts: the entry point's resource, and the theme, locale and language. */
type VersionLaunch = {
  resourceId: string
  resourceName: string
  themes: StartDataFromVersion['themes']
  locale: string
  language: string
}

/**
 * Run in the project header: launches the project's entry point. On Config that is the selected
 * version's, through the start-data dialog for a Process and the UI Flow run dialog for a UI Flow;
 * on Runtime, the build the active policy serves, through the same launch as the Projects list.
 *
 * Provide it, with `FlxThemesLoaderService`, on the component that hosts Run inside the project.
 */
@Injectable()
export class ProjectLaunchService {
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #startProcessService = inject(StartProcessService)
  #store = inject(Store)
  #flxModalService = inject(FlxModalService)
  #themeLoader = inject(FlxThemesLoaderService)
  #localeService = inject(LocaleService)

  #workspaceId = this.#store.selectSignal(selectWorkspaceId)
  #appId = this.#store.selectSignal(selectAppId)
  #appVersionId = this.#store.selectSignal(selectAppVersionId)

  /**
   * The version the store's settings were loaded for. The store keeps the previous version's settings
   * until the selected version's arrive, and the settings don't say which version they belong to, so
   * this notes the version shown each time they change.
   */
  #settingsVersionId: string | undefined

  constructor() {
    // An effect, not a linkedSignal: it has to note the version when the settings change, not when
    // Run is clicked, by which time the version may already be the next one.
    effect(() => {
      this.#applicationDetailsStore.applicationSettings()
      this.#settingsVersionId = untracked(this.#applicationDetailsStore.applicationVersion)?.id
    })
  }

  launchRuntime(): Promise<EntryPointLaunchResult> {
    return this.#startProcessService.startProcess({
      workspaceId: this.#workspaceId(),
      applicationId: this.#appId(),
      applicationName: this.#applicationDetailsStore.appName(),
    })
  }

  /**
   * Never launches a version other than the one selected, drafts included. Right after a version
   * switch it does nothing until that version's settings and entry points have arrived, rather than
   * start it from the previous version's; it then resolves to `null`.
   */
  async launchVersion(): Promise<EntryPointLaunchResult | null> {
    if (!this.#selectedVersionLoaded()) {
      return null
    }

    const settings = this.#applicationDetailsStore.applicationSettings()?.appVersionSettings
    const entryPoint = settings?.entrypoints?.WEB ?? settings?.entrypoints?.MOBILE

    if (!entryPoint) {
      showEntryPointNotSet(
        this.#flxModalService,
        this.#applicationDetailsStore.appName(),
        'selected_version'
      )

      return 'entry_point_missing'
    }

    await this.#themeLoader.init()
    const themeList = this.#themeLoader.themeList()

    const selectedTheme =
      themeList.find(({ flowxUuid }) => flowxUuid === settings.themeId) ||
      this.#themeLoader.defaultTheme()

    const locale = this.#localeService.selectedLocale() || DEFAULT_LOCALE

    const language =
      settings.languages?.find(({ isDefault }) => isDefault)?.code || DEFAULT_LANGUAGE

    const entryPointResource = Object.values(this.#applicationDetailsStore.entryPointsRx.value())
      .flat()
      .find(matchesEntryPoint(entryPoint))

    const launch: VersionLaunch = {
      resourceId: entryPointResource?.resourceId ?? entryPoint.resId,
      resourceName: entryPointResource?.resourceName ?? '',
      themes: { list: themeList, selected: selectedTheme },
      locale,
      language,
    }

    if (entryPoint.resType === ApplicationResourceType.UiFlow) {
      this.#openUiFlowRunDialog(launch)
    } else {
      this.#openStartDataDialog(launch)
    }

    return 'launched'
  }

  #openUiFlowRunDialog({
    resourceId,
    resourceName,
    themes,
    locale,
    language,
  }: VersionLaunch): void {
    const data: UiFlowRunDialogData = {
      themeId: themes.selected?.flowxUuid,
      themes: themes.list,
      locale,
      language,
      uiFlowId: resourceId,
      uiFlowName: resourceName,
    }

    this.#flxModalService.show(FlxUiFlowRunDialogComponent, START_DIALOG_CONFIG, data)
  }

  #openStartDataDialog({
    resourceId,
    resourceName,
    themes,
    locale,
    language,
  }: VersionLaunch): void {
    const fromVersion: StartDataFromVersion = {
      appVersionId: this.#appVersionId(),
      process: { resourceId, resourceName },
      themes,
    }

    this.#flxModalService.show(StartDataDialogComponent, START_DIALOG_CONFIG, {
      fromVersion,
      locale,
      language,
    })
  }

  /** Whether the settings and entry points in the store are the selected version's. */
  #selectedVersionLoaded(): boolean {
    return (
      this.#settingsVersionId === this.#appVersionId() &&
      this.#applicationDetailsStore.entryPointsRx.status() === 'resolved'
    )
  }
}
